import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchUser, selectUser } from '../userProfile/userSlice';
import { isUserAuthenticated } from '../../localstorage';

const selfPaths = ['/user-settings', '/user-settings/profile', '/user-settings/password'];

export default function AccessBoundary() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const authenticated = isUserAuthenticated();

  useEffect(() => {
    const recheck = () => { setVerified(false); setRefresh(value => value + 1); };
    const refreshAccess = () => setRefresh(value => value + 1);
    window.addEventListener('focus', refreshAccess);
    window.addEventListener('lto-access-changed', recheck);
    return () => {
      window.removeEventListener('focus', refreshAccess);
      window.removeEventListener('lto-access-changed', recheck);
    };
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    let active = true;
    setError(false);
    const request = dispatch(fetchUser());
    request.then(result => {
      if (!active) return;
      if (fetchUser.fulfilled.match(result) && result.payload.access) setVerified(true);
      else { setVerified(false); setError(true); }
    });
    return () => { active = false; request.abort(); };
  }, [authenticated, dispatch, location.pathname, refresh]);

  if (!authenticated) return <Navigate to="/login" replace />;
  if (error) return <div className="mx-auto max-w-lg p-8 text-center" role="alert">
    <h1 className="text-xl font-semibold">Unable to load your access</h1>
    <p className="my-4 text-slate-600">Please retry before continuing.</p>
    <button className="rounded-lg bg-red-600 px-5 py-3 text-white" onClick={() => setRefresh(value => value + 1)}>Retry</button>
    <a className="ml-5 underline" href="/login">Sign in again</a>
  </div>;
  // Recheck new paths in the background without remounting verified pages.
  // Initial verification and explicit access changes still block the outlet.
  if (!verified || !user.access) return <div role="status" className="p-8 text-center text-slate-600">Loading your workspace…</div>;

  const path = location.pathname.replace(/\/$/, '') || '/';
  const badgeRoute = path === '/airport-badges' || path.startsWith('/airport-badges/');
  const schedulerRoute = path === '/scheduler' || path.startsWith('/scheduler/');
  const access = user.access;
  const landing = access.landing_path || '/airport-badges';
  const allowedModule = (badgeRoute && access.can_access_airport_badges) || (schedulerRoute && access.can_access_scheduler);
  if (!access.can_access_operations && !allowedModule && !selfPaths.includes(path) && path !== landing) {
    return <Navigate to={landing} replace />;
  }
  if (schedulerRoute && !access.can_access_scheduler) {
    return <div role="alert" className="mx-auto max-w-lg p-8 text-center">
      <h1 className="text-xl font-semibold">Scheduler access is unavailable</h1>
      <p className="my-4 text-slate-600">Ask an administrator to review your access settings.</p>
      {access.can_access_operations && <a className="mr-5 underline" href="/">Return to LiveTakeoff</a>}
      <a className="underline" href="/login">Sign out</a>
    </div>;
  }
  if (badgeRoute && !access.can_access_airport_badges) {
    return <div role="alert" className="mx-auto max-w-lg p-8 text-center">
      <h1 className="text-xl font-semibold">Airport Badges access is unavailable</h1>
      <p className="my-4 text-slate-600">Ask an administrator to review your access settings.</p>
      {access.can_access_operations && <a className="mr-5 underline" href="/">Return to LiveTakeoff</a>}
      <a className="underline" href="/login">Sign out</a>
    </div>;
  }
  return <Outlet />;
}
